import { RapierSystemBehavior } from "./RapierSystemBehavior";
import { TransformBehavior } from "./TransformBehavior";
import { AttachmentRule, Behavior } from "./types";
import * as RAPIER from "@dimforge/rapier3d-compat";

// Character animation URLs
// const ANIMATION_URLS = {
//   JOG: "https://mmlstorage.com/d8b07672b2c3045b90955357f5f879653306f40e1db09828d67221dc22ccf52a",
//   IDLE: "https://mmlstorage.com/4083f4db4580d7cb1868dcb4fe36ddb3bc290fe91c84bc16a615aa91a30de251",
// } as const;

const ANIMATION_URLS = {
  JOG: "https://normie-6f4c23--normie-34ac53.msquaredhosting.com/filtered_anim_with_root_AS_Andor_Sprint_01.glb (1).glb",
  IDLE: "https://mmlstorage.com/4083f4db4580d7cb1868dcb4fe36ddb3bc290fe91c84bc16a615aa91a30de251",
  WALK: "https://normie-6f4c23--normie-34ac53.msquaredhosting.com/filtered_anim_with_root_AS_Andor_Sprint_01.glb (1).glb",
  JUMP: "https://normie-6f4c23--normie-34ac53.msquaredhosting.com/filtered_anim_no_root_AS_Andor_Stand_Jump_Loop.glb.glb",
  FALL: "https://normie-6f4c23--normie-34ac53.msquaredhosting.com/filtered_anim_with_root_AS_Andor_Float_01.glb.glb",
  DEAD: "https://normie-6f4c23--normie-34ac53.msquaredhosting.com/AS_Pose_Lying_Back_01.glb",
} as const;

export type AnimationState = "idle" | "moving" | "jump" | "fall" | "dead";

export type CharacterState = {
  currentPosition: { x: number; y: number; z: number };
  previousPosition: { x: number; y: number; z: number };
  currentAnimState: AnimationState;
  isMoving: boolean;
};

export class CharacterBehavior extends Behavior {
  private walkSpeed: number = 10; // units per second
  private jumpForce: number = 8; // jump impulse strength
  private destination: { x: number; y: number; z: number } | null = null;
  private characterState!: CharacterState;
  private hasReachedDestination: boolean = true;
  private destinationThreshold: number = 0.01;
  private isGrounded: boolean = false;
  private groundCheckDistance: number = 2.0;

  static getAttachmentRules(): AttachmentRule[] {
    return [{ type: "element", selector: "m-character" }];
  }

  onAttach(): void {
    console.log("CharacterBehavior attached to:", this.element);

    // Initialize character state
    this.characterState = this.createDefaultState();

    // Initialize current position and rotation from element attributes
    this.updateStateFromElement();

    // Initialize walk speed from element attribute if present
    const walkSpeedAttr = this.element.getAttribute("walkspeed");
    if (walkSpeedAttr) {
      const speed = parseFloat(walkSpeedAttr);
      if (!isNaN(speed) && speed > 0) {
        this.walkSpeed = speed;
      }
    }

    // Initialize jump force from element attribute if present
    const jumpAttr = this.element.getAttribute("jump-force");
    if (jumpAttr) {
      const force = parseFloat(jumpAttr);
      if (!isNaN(force) && force > 0) {
        this.jumpForce = force;
      }
    }

    // Check for destination attributes
    this.updateDestination();

    // Set initial animation state
    this.updateAnimationState();

    // Ensure appropriate physics setup
    this.ensureDynamicPhysics();
  }

  onDetach(): void {
    console.log("CharacterBehavior detached from:", this.element);
    this.characterState.isMoving = false;
  }

  private createDefaultState(): CharacterState {
    const position = { x: 0, y: 0, z: 0 };
    return {
      currentPosition: { ...position },
      previousPosition: { ...position },
      currentAnimState: "idle",
      isMoving: false,
    };
  }

  private updateStateFromElement(): void {
    const x = parseFloat(this.element.getAttribute("x") || "0");
    const y = parseFloat(this.element.getAttribute("y") || "0");
    const z = parseFloat(this.element.getAttribute("z") || "0");

    // Update previous position before current
    this.characterState.previousPosition = { ...this.characterState.currentPosition };
    this.characterState.currentPosition = { x, y, z };
  }

  private updateAnimationState(): void {
    let newAnimState: AnimationState;

    // Check if character is dead first - this overrides all other states
    if (this.element.classList.contains("dead")) {
      newAnimState = "dead";
    } else {
      // Get current Y velocity to determine air state
      const rigidBody = TransformBehavior.getRigidBodyForElement(this.element);
      const yVelocity = rigidBody ? rigidBody.linvel().y : 0;

      // Determine animation state based on grounded status and velocity
      if (this.isGrounded) {
        // On ground: use idle or moving based on horizontal movement
        newAnimState = this.characterState.isMoving ? "moving" : "idle";
      } else {
        // In air: use jump or fall based on Y velocity
        // Jump if going up or falling slowly (small negative velocity)
        // Fall if falling fast (significant negative velocity)
        const fallThreshold = -2.0; // Adjust this value as needed
        newAnimState = yVelocity > fallThreshold ? "jump" : "fall";
      }
    }

    if (newAnimState !== this.characterState.currentAnimState) {
      this.characterState.currentAnimState = newAnimState;

      switch (newAnimState) {
        case "moving":
          this.element.setAttribute("anim", ANIMATION_URLS.JOG);
          break;
        case "jump":
          this.element.setAttribute("anim", ANIMATION_URLS.JUMP);
          break;
        case "fall":
          this.element.setAttribute("anim", ANIMATION_URLS.FALL);
          break;
        case "dead":
          this.element.setAttribute("anim", ANIMATION_URLS.DEAD);
          break;
        default: // "idle"
          this.element.setAttribute("anim", ANIMATION_URLS.IDLE);
          break;
      }
    }
  }

  private ensureDynamicPhysics(): void {
    // Set the character to use dynamic physics for gravity and proper movement
    this.element.setAttribute("body-type", "dynamic");
    this.element.classList.add("rigidbody");

    // Set some reasonable physics properties
    if (!this.element.getAttribute("mass")) {
      this.element.setAttribute("mass", "1.0");
    }
    if (!this.element.getAttribute("linear-damping")) {
      this.element.setAttribute("linear-damping", "0.0");
    }
    if (!this.element.getAttribute("angular-damping")) {
      this.element.setAttribute("angular-damping", "10.0");
    }

    // Lock rotation on X and Z axes to prevent toppling, allow Y rotation for turning
    if (!this.element.getAttribute("lock-rotations")) {
      this.element.setAttribute("lock-rotations", "x,z");
    }
  }

  private checkGrounded(): boolean {
    const rigidBody = TransformBehavior.getRigidBodyForElement(this.element);
    if (!rigidBody) return false;

    const physicsWorld = RapierSystemBehavior.getPhysicsWorld();
    if (!physicsWorld) return false;

    const currentPos = rigidBody.translation();

    const characterCollider = TransformBehavior.getColliderForElement(this.element);

    // Cast a ray downward to check for ground
    const rayOrigin = { x: currentPos.x, y: currentPos.y - 0.1, z: currentPos.z };
    const rayDirection = { x: 0, y: -1, z: 0 };
    const ray = new RAPIER.Ray(rayOrigin, rayDirection);

    // Check slightly below the character's bottom
    const hit = physicsWorld.castRay(
      ray,
      100.0,
      true,
      // undefined,
      // undefined,
      // characterCollider,
    );

    if (hit != null) {
      //console.log(hit.timeOfImpact);
      if (hit.timeOfImpact < 0.3) {
        return true;
      }
    }

    return false;
  }

  /**
   * Public API: Apply movement to this character
   * This is the unified movement method that other behaviors can call
   */
  public applyMovement(
    movementVector: { x: number; y: number; z: number },
    deltaTime: number,
    speed?: number,
  ): void {
    // Dead characters cannot move
    if (this.element.classList.contains("dead")) {
      return;
    }

    const rigidBody = TransformBehavior.getRigidBodyForElement(this.element);
    if (!rigidBody) {
      console.warn("CharacterBehavior: No rigidbody found, cannot apply movement");
      return;
    }

    let moveSpeed = speed || this.walkSpeed;
    moveSpeed = 10;

    // Check if character is grounded
    this.isGrounded = this.checkGrounded();

    // Get current velocity
    const currentVelocity = rigidBody.linvel();

    // Calculate desired horizontal velocity directly
    const desiredHorizontalVelocity = {
      x: movementVector.x * moveSpeed,
      z: movementVector.z * moveSpeed,
    };

    // Update movement state for horizontal movement only
    const horizontalMovementLength = Math.sqrt(
      movementVector.x * movementVector.x + movementVector.z * movementVector.z,
    );

    const wasMoving = this.characterState.isMoving;
    this.characterState.isMoving = horizontalMovementLength > 0.01;

    // Update rotation to face movement direction if moving horizontally
    if (
      this.characterState.isMoving &&
      (Math.abs(movementVector.x) > 0.01 || Math.abs(movementVector.z) > 0.01)
    ) {
      const yaw = Math.atan2(movementVector.x, movementVector.z) * (180 / Math.PI);
      this.element.setAttribute("ry", yaw.toString());
    }

    // Set velocity directly
    rigidBody.setLinvel(
      {
        x: desiredHorizontalVelocity.x,
        y: currentVelocity.y, // Preserve Y velocity for gravity
        z: desiredHorizontalVelocity.z,
      },
      true,
    );

    // Handle jumping (only when grounded and Y input is positive)
    if (movementVector.y > 0.01 && this.isGrounded) {
      rigidBody.setLinvel(
        {
          x: desiredHorizontalVelocity.x,
          y: this.jumpForce,
          z: desiredHorizontalVelocity.z,
        },
        true,
      );
      console.log("CharacterBehavior: Jumping with velocity", this.jumpForce);
    }

    // Update character state from physics
    const newPos = rigidBody.translation();
    this.characterState.previousPosition = { ...this.characterState.currentPosition };
    this.characterState.currentPosition = { x: newPos.x, y: newPos.y, z: newPos.z };

    // Update animation state if movement state changed
    if (wasMoving !== this.characterState.isMoving) {
      this.updateAnimationState();
    }
  }

  private updateDestination(): void {
    const dx = this.element.getAttribute("dx");
    const dy = this.element.getAttribute("dy");
    const dz = this.element.getAttribute("dz");

    if (dx !== null || dy !== null || dz !== null) {
      this.destination = {
        x: dx !== null ? parseFloat(dx) : this.characterState.currentPosition.x,
        y: dy !== null ? parseFloat(dy) : this.characterState.currentPosition.y,
        z: dz !== null ? parseFloat(dz) : this.characterState.currentPosition.z,
      };

      // Check if we're not already at the destination
      const distance = this.getDistanceToDestination();
      this.hasReachedDestination = distance <= this.destinationThreshold;
    } else {
      this.destination = null;
      this.hasReachedDestination = true;
    }
  }

  private getDistanceToDestination(): number {
    if (!this.destination) return 0;

    const dx = this.destination.x - this.characterState.currentPosition.x;
    const dy = this.destination.y - this.characterState.currentPosition.y;
    const dz = this.destination.z - this.characterState.currentPosition.z;

    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  private moveTowardsDestination(deltaTime: number): void {
    if (!this.destination) return;

    // Dead characters cannot move towards destinations
    if (this.element.classList.contains("dead")) {
      return;
    }

    // Update position tracking from element
    this.updateStateFromElement();

    // Calculate direction to destination
    const dx = this.destination.x - this.characterState.currentPosition.x;
    const dy = this.destination.y - this.characterState.currentPosition.y;
    const dz = this.destination.z - this.characterState.currentPosition.z;

    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

    // Check if we've reached the destination
    if (distance < this.destinationThreshold) {
      this.hasReachedDestination = true;
      // Stop movement by applying zero movement vector
      this.applyMovement({ x: 0, y: 0, z: 0 }, deltaTime);
      return;
    }

    // Normalize direction and apply movement
    const direction = {
      x: dx / distance,
      y: dy / distance,
      z: dz / distance,
    };

    this.applyMovement(direction, deltaTime);
  }

  tick(deltaTime: number): void {
    // Update grounded state for animation system
    this.isGrounded = this.checkGrounded();

    // Always update animation state (including for dead characters)
    this.updateAnimationState();

    // Dead characters don't process movement logic
    if (this.element.classList.contains("dead")) {
      return;
    }

    // Check if destination attributes have changed
    const currentDx = this.element.getAttribute("dx");
    const currentDy = this.element.getAttribute("dy");
    const currentDz = this.element.getAttribute("dz");

    const hasDestination = currentDx !== null || currentDy !== null || currentDz !== null;
    const destinationChanged =
      !this.destination ||
      (currentDx !== null && parseFloat(currentDx) !== this.destination.x) ||
      (currentDy !== null && parseFloat(currentDy) !== this.destination.y) ||
      (currentDz !== null && parseFloat(currentDz) !== this.destination.z);

    if (hasDestination && destinationChanged) {
      // Update destination
      this.destination = {
        x: currentDx ? parseFloat(currentDx) : this.destination?.x || 0,
        y: currentDy ? parseFloat(currentDy) : this.destination?.y || 0,
        z: currentDz ? parseFloat(currentDz) : this.destination?.z || 0,
      };
      this.hasReachedDestination = false;
    }

    // Move towards destination if we have one and haven't reached it
    if (hasDestination && !this.hasReachedDestination) {
      this.moveTowardsDestination(deltaTime);
    } else if (!hasDestination) {
      // No destination, stop moving
      this.applyMovement({ x: 0, y: 0, z: 0 }, deltaTime);
    }

    // Check if walkspeed has changed
    const walkSpeedAttr = this.element.getAttribute("walkspeed");
    if (walkSpeedAttr) {
      const speed = parseFloat(walkSpeedAttr);
      if (!isNaN(speed) && speed > 0 && speed !== this.walkSpeed) {
        this.walkSpeed = speed;
      }
    }

    // Check if jump force has changed
    const jumpAttr = this.element.getAttribute("jump-force");
    if (jumpAttr) {
      const force = parseFloat(jumpAttr);
      if (!isNaN(force) && force > 0 && force !== this.jumpForce) {
        this.jumpForce = force;
      }
    }
  }
}
